import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import moment from 'moment'
import { MainHeading, Divider, DatePicker, SpinnerDotted, Spinner } from '../../components/common/atoms'
import { Table, Pagination } from '../../components/common/molecules'
import { handleAppError } from '../../components/handlers'
import { RaiseDisputeModal } from '../../components/modals'
import { successOrFailureToaster } from '../../components/common/atoms/messages'
import { PARTICIPATION_BREAK_UP_HEADERS, PARTICIPATION_LIST_HEADERS } from '../../constants/tableHeaders'
import { invoiceBreakUpSchema, misParticipantsListSchema } from '../../schemas/table'
import { popup_close as closebtn, white_tick_icon, x_red, exclametry_red_dark } from '../../assets/images'
import InvoicesApi from '../../services/invoices.api'
import { SALES_ROLE } from '../../constants/overview'

const getBreakUpHeaders = (service, gst_percentage) => {
  service = (service || '').trim().toLowerCase()

  let headers = []
  if(['health checks', 'vaccination', 'vision', 'dental', 'onsite', 'engagement activity', 'ohc'].includes(service)) headers = PARTICIPATION_BREAK_UP_HEADERS['ppu_headers'](service, gst_percentage)
  else if(['doctor consultation', 'opd', 'ewap', 'smart cover'].includes(service)) headers = PARTICIPATION_BREAK_UP_HEADERS['subscription_headers'](gst_percentage)
  else {
    const callback = PARTICIPATION_BREAK_UP_HEADERS[service.replace(/ /g, '_')]
    headers = callback ? callback(gst_percentage) : []
  }

  return headers
}

const getParticipationListHeaders = (service) => {
  service = (service || '').trim().toLowerCase()

  let headers = []
  if(['health checks', 'vaccination', 'vision', 'dental', 'onsite', 'engagement activity', 'ohc'].includes(service)) headers = PARTICIPATION_LIST_HEADERS['ppu_headers']
  else if(['doctor consultation', 'opd', 'ewap', 'smart cover', 'gym', 'employee addition', 'health coach'].includes(service)) headers = PARTICIPATION_LIST_HEADERS['subscription_headers']

  return headers
}

const MISInvoices = () => {
  const location = useLocation()
  const history = useHistory()
  const pathState = location && location.state 
  const [data, setData] = useState({ table_loading: true, summary_loading: true })
  const [paginationData, setPaginationData] = useState({ total_pages: 0, cur_page: 0, total_results: 0 })
  const [flags, setFlags] = useState({disputeModal: false, spinner: false})
  const dates = useRef({ start_date: pathState.start_date || moment().startOf('month').format('DD-MM-YYYY'), end_date: pathState.end_date || moment().endOf('month').format('DD-MM-YYYY')})

  const fetchData = () => {
    setData(prevState => { return {...prevState, table_loading: true, summary_loading: true } })
    const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id, page: 0 }
    const promise1 = InvoicesApi.fetch_mis_summary({ data })
    const promise2 = InvoicesApi.fetch_participants_list({ data })
    
    Promise.all([promise1, promise2])
    .then((values) => {
      const resp_arr = values.map((resp) => resp.data)
      const total_results = (resp_arr[1].meta && resp_arr[1].meta.total) ||  0
      setPaginationData({ total_pages: Math.ceil(total_results / 10), cur_page: 0, total_results })
      setData({ ...resp_arr[0], ...resp_arr[1], table_loading: false, summary_loading: false })
    })
    .catch((err) => {
      handleAppError(err)
      setData(prevState => { return {...prevState, table_loading: false, summary_loading: false } })
    })
  }

  const navigateToPage = (page) => {
    setData(prevState => { return {...prevState, table_loading: true} })
    InvoicesApi.fetch_participants_list({ data: { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id, page } },
      (res) => {
        const data = res.data || {}
        setPaginationData(prevState => { return {...prevState, cur_page: page} })
        setData(prevState => { return {...prevState, ...data, table_loading: false}})
      },
      (err) => {
        handleAppError(err)
        setData(prevState => { return {...prevState, table_loading: false } })
      }
    )
  }

  const disputeHandler = (type, comment) => {
    if(['close', 'open'].includes(type)) {
      setFlags(prevState => { return {...prevState, disputeModal: !prevState.disputeModal}})
    } else if(type === 'submit') {
      setFlags({ spinner: true, disputeModal: false })
      const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id, comment }
      InvoicesApi.dispute_invoice({ data },
        (_) => {
          setFlags({ spinner: false, disputeModal: false })
          fetchData()
          successOrFailureToaster({ message: 'Invoice has been rejected', type: 'custom', customColor: '#C80010', customIcon: exclametry_red_dark })
        }, 
        (err) => {
          const message = (err.response && err.response.data && err.message) || 'Error occurred while raising dispute'
          setFlags({ spinner: false, disputeModal: false })
          successOrFailureToaster({ message, type: 'fail'})
        }
      )
    }
  }

  const confirmHandler = () => {
    setFlags({ spinner: true, disputeModal: false})
    const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id }
    InvoicesApi.confirm_invoice({ data },
      (_) => {
        setFlags({ spinner: false, disputeModal: false })
        fetchData()
        successOrFailureToaster({ message: 'Invoice has been confirmed' })
      }, 
      (err) => {
        const message = (err.response && err.response.data && err.message) || 'Error occurred while confirming invoice'
        setFlags({ spinner: false, disputeModal: false })
        successOrFailureToaster({ message, type: 'fail'})
      }
    )
  }

  const downloadHadnler = () => {
    setFlags({ spinner: true, disputeModal: false})
    const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id }
    InvoicesApi.download_participation_list({ data },
      (res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }))
        const link = document.createElement('a')
        const fileName = res.headers['content-type'] === 'application/pdf' ? 'participation list.pdf' : 'participation list.csv'
        link.href = url
        link.setAttribute('download', fileName)
        link.click()
        link.remove()

        setFlags({ spinner: false, disputeModal: false })
      },
      (err) => {
        const message = (err.response && err.response.data && err.response.data.msg) || 'Error occurred while downloading participation list'
        setFlags({ spinner: false, disputeModal: false })
        successOrFailureToaster({ message, type: 'fail'})
      }
    )
  }

  useEffect(() => {
    fetchData()
  }, [])

  const service = (pathState.service || '').trim().toLowerCase()
  const isInvoiceRejected = (data.invoice_status || '').trim().toLowerCase() === 'rejected'
  const isInvoiceConfirmed = ['confirmed', 'auto confirmed'].includes((data.invoice_status || '').trim().toLowerCase())
  const isStaff = (sessionStorage.getItem('role') || '').toLowerCase() === 'staff'
  const tableData = (data.appointments || data.customers || [])

  return (
    <Spinner visible={flags.spinner} >
      <div className='min-h-screen bg-gray-100 p-8'>
        <div className='flex justify-between items-center'>
          <MainHeading mainHeading={location.state.service} />
          <img src={closebtn} alt='close-btn' className='cursor-pointer h-8 w-8' onClick={() => history.goBack()} />
        </div>

        <Divider classes='mt-5 mb-6' />

        {/* summary */}
        <div className={`bg-gray-200 rounded-lg p-6 ${!data.summary_loading ? '':'flex items-center justify-center h-44'}`}>
        {!data.summary_loading ?
          <React.Fragment>
            <div className='flex'>
              <table className='w-4/5 border-separate'>
                <thead>
                  <tr>
                    {
                      getBreakUpHeaders(service, data.gst_percentage || 0).map(item => <th className='text-gray-500'>{item}</th>)
                    }
                  </tr>
                </thead>
                <tbody className='text-gray-900'>
                  {
                    (data['invoice_item_details'] || []).map(item_data => {
                      return (
                        invoiceBreakUpSchema(service, item_data)
                      )
                    })
                  }
                </tbody>
              </table>
              <Divider type='vertical' classes='mt-2 border-gray-275' />
              <div className='w-1/5 font-semibold flex flex-col items-center justify-center'>
                <h1 className='text-gray-500 font-semibold'>AMOUNT TO BE PAID</h1>
                <span className='text-gray-700'>₹{data['total_amount'] || 0}</span>
                { (isInvoiceConfirmed || isInvoiceRejected) && <span className={`font-semibold rounded-3xl px-2 py-1 mt-1 ${isInvoiceConfirmed ? 'text-green-1000 bg-green-100':'text-red-600 bg-red-50'}`}>{data.invoice_status}</span> }
              </div>
            </div>
            { !isInvoiceConfirmed && (isStaff || isInvoiceRejected) &&
              <React.Fragment>
                <Divider classes='border-gray-275 my-6' />
                <div className={`flex ${isInvoiceRejected ? 'flex-col' : 'justify-center items-center'}`}>
                <p className='mb-0 text-gray-900 font-semibold'>{ isInvoiceRejected ? 'Reason for rejection' : 'Please accept the invoice by 06th of every month or else invoice will be auto confirmed'}</p>
                  { isInvoiceRejected ?
                    <React.Fragment>
                      <p className='bg-white mb-0 mt-3 rounded-md p-4 text-gray-900 font-semibold'>{data.comment}</p>
                    </React.Fragment>
                    :
                    <React.Fragment>
                      <button
                        onClick={confirmHandler} 
                        disabled={SALES_ROLE === "sales"}
                        className={`text-white bg-blue-600 text-base rounded-md font-semibold px-6 py-3.5 ml-16 flex items-center justify-center focus:outline-none ${SALES_ROLE === 'sales' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <img src={white_tick_icon} alt='white-tick' className='inline mr-2' />
                        Confirm
                      </button>
                      <button 
                        onClick={() => disputeHandler('open')}
                        disabled={SALES_ROLE === "sales"}
                        className={`text-blue-600 bg-white text-base rounded-md border border-gray-275 font-semibold px-6 py-3.5 ml-3 flex items-center justify-center focus:outline-none ${SALES_ROLE === 'sales' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <img src={x_red} alt='red-close' className='inline mr-2' />
                        Raise dispute
                      </button>
                    </React.Fragment>
                  }
                </div>
              </React.Fragment>
            }
          </React.Fragment>
          :
          <SpinnerDotted />
        }
        </div>


        {/* filter */}
        <div className='bg-white rounded-lg px-4 py-2 flex justify-between mt-8'>
          <div className='flex items-center'>
            <span className='font-semibold text-gray-300 mr-3'>FROM DATE</span><DatePicker classes='mr-6' value={dates.current.start_date} disabled={true} />
            <span className='font-semibold text-gray-300 mr-3'>TO DATE</span><DatePicker value={dates.current.end_date} disabled={true} />
          </div>
          <button 
            onClick={downloadHadnler}
            disabled={SALES_ROLE === "sales"}
            className={`text-blue-600 font-semibold text-base px-4 py-2 border border-gray-275 rounded-md ${SALES_ROLE === 'sales' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            Download participants list
          </button>
        </div>

        {/* participation list */}
        <Table 
          loading={data.table_loading}
          headers={getParticipationListHeaders(service)}
          tableschema={misParticipantsListSchema(service)}
          data={tableData}
          classes='mt-6 rounded-lg'
          tableClasses='alternate_gray200_color'
        />
        { !data.table_loading && tableData.length > 0 && 
          <Pagination 
            totalpages={paginationData.total_pages}
            totalresults={paginationData.total_results}
            curpage={paginationData.cur_page}
            handler={navigateToPage}
          />
        }
        <RaiseDisputeModal visible={flags.disputeModal} service={pathState.service || ''} bill_amount={data['total_amount'] || 0} close={() => disputeHandler('close')} submitHandler={(v) => disputeHandler('submit', v)} />
      </div>
    </Spinner>
  )
}

export default MISInvoices